import { Navigate, Outlet, Route, Routes, useLocation } from "react-router-dom";

import { useSession } from "@/api/queries";
import { AppLayout } from "@/components/AppLayout";
import { CommunityView } from "@/views/CommunityView";
import { DiscoverView } from "@/views/DiscoverView";
import { AccountView } from "@/views/AccountView";
import { HomeView } from "@/views/HomeView";
import { LoginView } from "@/views/LoginView";
import { AudienceView } from "@/views/studio/AudienceView";
import { AuditView } from "@/views/studio/AuditView";
import { CanonView } from "@/views/studio/CanonView";
import { ChecksView } from "@/views/studio/ChecksView";
import { ClassifyView } from "@/views/studio/ClassifyView";
import { ExportView } from "@/views/studio/ExportView";
import { GenerateView } from "@/views/studio/GenerateView";
import { GrowthView } from "@/views/studio/GrowthView";
import { OverviewView } from "@/views/studio/OverviewView";
import { ReviewView } from "@/views/studio/ReviewView";
import { ScriptView } from "@/views/studio/ScriptView";
import { SettingsView } from "@/views/studio/SettingsView";
import { SimulateView } from "@/views/studio/SimulateView";
import { StoryView } from "@/views/studio/StoryView";
import { SystemView } from "@/views/studio/SystemView";

/**
 * 创作台访问控制（`specs/15` §2 · PR-1508 · `ADR-025` D1）。
 *
 * - 未登录 / 会话失效 → 重定向 `/login`（带回跳路径）；
 * - 已登录但**当前身份**是观众 → 观众侧 `/discover`；
 * - 当前身份是编剧 → 放行。
 *
 * 判据是 `GET /auth/me`（`useSession`）的**真实身份**，不是本地 token 是否存在——
 * token 可能已过期/被撤销，只有后端能判有效性（前端不是权限边界，CONSTITUTION A3）。
 *
 * ⚠️ 这里判 `view_mode`（**当前身份**）而不是 `platform_role`（**授权**）：
 * 编剧切到观众视角后会被引导出创作台，但创作权限在数据层**仍然保留**，切回即恢复
 * （`ADR-025` D1）。判 `platform_role` 就等于把"切个视图"变成一次真的降权。
 */
function RequireEditorView() {
  const session = useSession();
  const location = useLocation();

  if (session.isPending) {
    return <div className="page-scroll" aria-busy="true" />;
  }
  const user = session.data;
  if (session.isError || !user) {
    return <Navigate to="/login" state={{ from: location.pathname }} replace />;
  }
  if (user.view_mode !== "editor") {
    return <Navigate to="/discover" replace />;
  }
  return <Outlet />;
}

/** 路由表（specs/07 §4 的视图清单）。 */
export function App() {
  return (
    <Routes>
      <Route element={<AppLayout />}>
        {/* 平台 / 观众侧 */}
        <Route path="/" element={<HomeView />} />
        <Route path="/discover" element={<DiscoverView />} />
        <Route path="/community" element={<CommunityView />} />

        {/* 账号（specs/15 §2）：登录 / 注册 / 账号与身份 */}
        <Route path="/login" element={<LoginView />} />
        {/*
          `/account` **刻意在 editor 门外**（`ADR-025` D6）：观众要看自己的角色变更记录、
          也要知道自己为什么切不到编剧身份。放进 `RequireEditorView` 就等于对玩家不存在。
          视图内部自己处理未登录（引导去 `/login`），不留空白页。
        */}
        <Route path="/account" element={<AccountView />} />

        {/* 专业创作台：需当前身份为编剧（specs/15 §2 · PR-1508） */}
        <Route element={<RequireEditorView />}>
          <Route path="/studio" element={<Navigate to="/studio/overview" replace />} />
          <Route path="/studio/overview" element={<OverviewView />} />
          <Route path="/studio/classify" element={<ClassifyView />} />
          <Route path="/studio/canon" element={<CanonView />} />
          <Route path="/studio/story" element={<StoryView />} />
          <Route path="/studio/script" element={<ScriptView />} />
          <Route path="/studio/checks" element={<ChecksView />} />
          <Route path="/studio/simulate" element={<SimulateView />} />
          <Route path="/studio/generate" element={<GenerateView />} />
          <Route path="/studio/review" element={<ReviewView />} />
          <Route path="/studio/export" element={<ExportView />} />
          <Route path="/studio/growth" element={<GrowthView />} />
          <Route path="/studio/audit" element={<AuditView />} />
          <Route path="/studio/audience" element={<AudienceView />} />
          <Route path="/studio/system" element={<SystemView />} />
          {/* 安装级配置（specs/07 §4 · specs/13）：与任何项目无关，故不随项目切换 */}
          <Route path="/studio/settings" element={<SettingsView />} />
        </Route>

        <Route path="*" element={<Navigate to="/" replace />} />
      </Route>
    </Routes>
  );
}
